import React, { useState, useEffect } from 'react';
import { Trophy, Gift } from 'lucide-react';

export default function ChallengeProgress({ challenge, isLoading = false }) {

  const getDaysRemaining = () => {
    if (!challenge?.endDate) return 0;
    const endDate = new Date(challenge.endDate);
    const today = new Date();
    const diffTime = endDate - today;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const renderBookIcons = () => {
    const icons = [];
    const { targetBooks = 10, currentBooks = 0 } = challenge || {};
    
    for (let i = 1; i <= targetBooks; i++) {
      const isCompleted = i <= currentBooks;
      icons.push(
        <div
          key={i}
          className={`w-6 h-8 rounded flex items-center justify-center text-xs font-semibold text-white ${
            isCompleted 
              ? 'bg-violet-500' 
              : 'bg-stone-300 text-stone-600'
          }`}
        >
          {i}
        </div>
      );
    }
    
    return icons;
  };

  if (isLoading || !challenge) {
    return (
      <div className="bg-white rounded-2xl p-5 border border-violet-200 shadow-sm border-l-4 border-l-violet-500">
        <div className="flex items-center gap-2 mb-4">
          <Trophy className="w-5 h-5 text-violet-600" />
          <h2 className="text-lg font-semibold text-slate-900">Reading Challenge</h2>
        </div>
        
        <div className="text-center py-8 text-stone-500">
          <Trophy className="w-12 h-12 mx-auto mb-4 text-stone-300 animate-pulse" />
          <p>Loading your challenge...</p>
        </div>
      </div>
    );
  }

  if (!challenge && !isLoading) {
    return (
      <div className="bg-white rounded-2xl p-5 border border-violet-200 shadow-sm border-l-4 border-l-violet-500">
        <div className="flex items-center gap-2 mb-4">
          <Trophy className="w-5 h-5 text-violet-600" />
          <h2 className="text-lg font-semibold text-slate-900">Reading Challenge</h2>
        </div>
        
        <div className="text-center py-8">
          <Trophy className="w-12 h-12 mx-auto mb-4 text-stone-300" />
          <h3 className="text-lg font-semibold text-stone-900 mb-2">No Active Challenge</h3>
          <p className="text-stone-600 mb-4">Ready to take on a reading challenge?</p>
          <button className="px-6 py-3 bg-violet-500 text-white font-semibold rounded-xl hover:bg-violet-600 transition-colors">
            Start Challenge
          </button>
        </div>
      </div>
    );
  }

  const progressPercentage = (challenge.currentBooks / challenge.targetBooks) * 100;
  const daysRemaining = getDaysRemaining();

  return (
    <div className="bg-white rounded-2xl p-5 border border-violet-200 shadow-sm border-l-4 border-l-violet-500">
      <div className="flex items-center gap-2 mb-4">
        <Trophy className="w-5 h-5 text-violet-600" />
        <h2 className="text-lg font-semibold text-slate-900">{challenge.name}</h2>
      </div>

      <div className="bg-violet-50 rounded-xl p-4 text-center">
        {/* Book Progress Icons */}
        <div className="flex justify-center gap-2 mb-3 flex-wrap">
          {renderBookIcons()}
        </div>

        {/* Progress Text */}
        <div className="text-sm text-violet-700 font-medium mb-3">
          {challenge.currentBooks} of {challenge.targetBooks} books completed • {daysRemaining} days left
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-violet-200 rounded-full h-3 mb-4">
          <div 
            className="bg-violet-500 h-3 rounded-full transition-all duration-500"
            style={{ width: `${progressPercentage}%` }}
          ></div>
        </div>

        {/* Reward Preview */}
        <div className="bg-violet-100 rounded-lg p-3 flex items-center justify-center gap-2">
          <Gift className="w-4 h-4 text-violet-700" />
          <div className="text-sm text-violet-700 font-semibold">
            Finish challenge = {challenge.rewardDescription}
          </div>
        </div>

        {/* Encouragement Message */}
        {challenge.currentBooks > 0 && (
          <div className="mt-3 text-xs text-violet-600">
            {challenge.currentBooks >= challenge.targetBooks * 0.8 
              ? "You're so close! Keep going!" 
              : challenge.currentBooks >= challenge.targetBooks * 0.5
              ? "Great progress! You're halfway there!"
              : "Awesome start! Keep up the momentum!"
            }
          </div>
        )}
      </div>

      {/* Quick Stats */}
      <div className="mt-4 grid grid-cols-3 gap-4 text-center text-xs text-stone-600">
        <div>
          <div className="font-semibold text-violet-600">
            {Math.round(progressPercentage)}%
          </div>
          <div>Complete</div>
        </div>
        <div>
          <div className="font-semibold text-violet-600">
            {challenge.targetBooks - challenge.currentBooks}
          </div>
          <div>Books Left</div>
        </div>
        <div>
          <div className="font-semibold text-violet-600">
            ${challenge.rewardAmount?.toFixed(2) || '0.00'}
          </div>
          <div>Reward</div>
        </div>
      </div>
    </div>
  );
}